import { MenuItem } from "@mui/material";
import { Form } from "formik";
import moment from "moment";
import * as yup from "yup";
import { convertDateTimeToNet } from "../../../utils/datetimeUtils";
import { FormikDateRangeInput, FormikDateRangeSearch } from "../../commons/formik/formikDateRangeSearch";
import FormikForm from "../../commons/formik/formikForm";
import FormikSelect from "../../commons/formik/formikSelect";
import FormikTextField from "../../commons/formik/formikTextField";
import { StyledForm } from "../../styled/form";
import { StyledPaper } from "../../styled/paper";

yup.setLocale({
  mixed: {
    notType: 'Campo inválido'
  }
});

yup.addMethod(yup.Schema, 'atLeastOne', function (fields) {
  return this.test({
    name: 'atLeastOne',
    message: 'Debe completar al menos uno de estos campos',
    test: function (value, textContext) {
      const { parent, createError, path } = textContext;
      return fields.some(f => {
        return parent[f] != null && parent[f] !== ""
      })
        ? true
        : createError({ path, message: "Debe completar al menos uno de estos campos" });
    }
  });
});

// We make idArt NOT required. If it's missing, the global search looks in all permitted.
const arrayAtLeastOne = ["nroReferencia", "idConversacionExterna", "documentoTrabajador", "idEstadoConversacion", "idArt", "dateFrom", "dateTo"]

const validationSchema = yup.object().shape({
  idArt: yup.string().required('El campo es obligatorio').atLeastOne(arrayAtLeastOne),
  idConversacionExterna: yup.string().notRequired().atLeastOne(arrayAtLeastOne),
  nroReferencia: yup.string().notRequired().atLeastOne(arrayAtLeastOne),
  documentoTrabajador: yup.string().notRequired().atLeastOne(arrayAtLeastOne),
  idEstadoConversacion: yup.string().notRequired().atLeastOne(arrayAtLeastOne),
  incluirCerrados: yup.boolean().notRequired(),
  datePeriod: yup.string().optional(),
  dateFrom: yup
    .date()
    .nullable()
    .notRequired()
    .when('dateTo', {
      is: (dateTo) => dateTo !== null && dateTo !== undefined,
      then: (schema) => schema.required('Ingrese una fecha desde la que buscar'),
    })
    .test('date-from-before-to', 'La fecha desde debe ser anterior o igual a la fecha hasta',
      function (value) {
        const { dateTo } = this.parent;
        if (!value || !dateTo) return true;
        return new Date(value) <= new Date(dateTo);
      }
    )
    .atLeastOne(arrayAtLeastOne),
  dateTo: yup
    .date()
    .nullable()
    .notRequired()
    .when('dateFrom', {
      is: (dateFrom) => dateFrom !== null && dateFrom !== undefined,
      then: (schema) => schema.required('Ingrese una fecha hasta la que buscar'),
    })
    .test('date-to-after-from', 'La fecha hasta debe ser posterior o igual a la fecha desde',
      function (value) {
        const { dateFrom } = this.parent;
        if (!value || !dateFrom) return true;
        return new Date(value) >= new Date(dateFrom);
      }
    )
    .atLeastOne(arrayAtLeastOne),
}, [['dateFrom', 'dateTo']]);

const validationSchemaDateOnly = yup.object().shape({
  datePeriod: yup.string().optional(),
  dateFrom: yup
    .date()
    .nullable()
    .notRequired()
    .when('dateTo', {
      is: (dateTo) => dateTo !== null && dateTo !== undefined,
      then: (schema) => schema.required('Ingrese una fecha desde la que buscar'),
    })
    .test('date-from-before-to', 'La fecha desde debe ser anterior o igual a la fecha hasta',
      function (value) {
        const { dateTo } = this.parent;
        if (!value || !dateTo) return true;
        return new Date(value) <= new Date(dateTo);
      }
    ),
  dateTo: yup
    .date()
    .nullable()
    .notRequired()
    .when('dateFrom', {
      is: (dateFrom) => dateFrom !== null && dateFrom !== undefined,
      then: (schema) => schema.required('Ingrese una fecha hasta la que buscar'),
    })
    .test('date-to-after-from', 'La fecha hasta debe ser posterior o igual a la fecha desde',
      function (value) {
        const { dateFrom } = this.parent;
        if (!value || !dateFrom) return true;
        return new Date(value) >= new Date(dateFrom);
      }
    ),
}, [['dateFrom', 'dateTo']]);

const initialValues = {
  idArt: "",
  nroReferencia: "",
  idConversacionExterna: "",
  documentoTrabajador: "",
  idEstadoConversacion: "",
  incluirCerrados: false,
  datePeriod: "all",
  dateFrom: null,
  dateTo: null,
};

const MessagingSearchForm = ({ invalid, handleSearch, handleReset, art, dateOnly = false }) => {
  const handleSubmitSearch = (values) => {

    const searchModel = dateOnly ? {} : {
      idArt: values.idArt || null,
      nroReferencia: values.nroReferencia || null,
      idConversacionExterna: values.idConversacionExterna || null,
      documentoTrabajador: values.documentoTrabajador || null,
      idEstadoConversacion: values.idEstadoConversacion ? Number(values.idEstadoConversacion) : null,
      incluirCerrados: values.incluirCerrados === true || values.incluirCerrados === "true",
    };

    if (values.dateFrom && values.dateTo) {
      searchModel.fechaDesde = convertDateTimeToNet(moment(values.dateFrom).startOf("day"));
      searchModel.fechaHasta = convertDateTimeToNet(moment(values.dateTo).endOf("day"));
    }

    handleSearch(searchModel);
  };

  return (
    <div>
      <StyledPaper.Container>
        <FormikForm
          initialValues={initialValues}
          validationSchema={dateOnly ? validationSchemaDateOnly : validationSchema}
          onSubmit={handleSubmitSearch}>
          {({ handleSubmit, setFieldValue }) => (
            <>
              <Form>
                {!dateOnly && (
                  <>
                    <StyledForm.Input>
                      <FormikSelect
                        name="idArt"
                        label="Art"
                        variant="standard"
                        size="medium">
                        <MenuItem value="">Todas</MenuItem>
                        {art.map((a) => (
                          <MenuItem value={a.id} key={a.id}>
                            {a.nombre}
                          </MenuItem>
                        ))}
                      </FormikSelect>
                    </StyledForm.Input>

                    <StyledForm.Input>
                      <FormikTextField
                        name="idConversacionExterna"
                        label="Id Conversación"
                        variant="standard"
                      />
                    </StyledForm.Input>

                    <StyledForm.Input>
                      <FormikTextField
                        name="nroReferencia"
                        label="Siniestro"
                        variant="standard"
                      />
                    </StyledForm.Input>

                    <StyledForm.Input>
                      <FormikTextField
                        name="documentoTrabajador"
                        label="Documento Trabajador"
                        variant="standard"
                      />
                    </StyledForm.Input>

                    <StyledForm.Input>
                      <FormikSelect
                        name="idEstadoConversacion"
                        label="Estado"
                        variant="standard"
                      >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="1">Esperando Respuesta ART</MenuItem>
                        <MenuItem value="2">Esperando Respuesta Prestador</MenuItem>
                        <MenuItem value="3">Otro estado...</MenuItem>
                      </FormikSelect>
                    </StyledForm.Input>

                    <StyledForm.Input>
                      <FormikSelect
                        name="incluirCerrados"
                        label="Incluir Cerradas"
                        variant="standard"
                      >
                        <MenuItem value={false}>No</MenuItem>
                        <MenuItem value={true}>Sí</MenuItem>
                      </FormikSelect>
                    </StyledForm.Input>
                  </>
                )}

                <StyledForm.Input>
                  <FormikDateRangeSearch
                    name="datePeriod"
                    label="Período (Último Mensaje)"
                    variant="standard"
                    startDateFieldName="dateFrom"
                    endDateFieldName="dateTo"
                  />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikDateRangeInput
                    name="dateFrom"
                    label="Fecha Desde"
                  />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikDateRangeInput
                    name="dateTo"
                    label="Fecha Hasta"
                  />
                </StyledForm.Input>
              </Form>
              <StyledForm.ButtonContainer>
                <StyledForm.Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={invalid}>
                  Buscar
                </StyledForm.Button>
              </StyledForm.ButtonContainer>
              <StyledForm.ButtonContainer>
                <StyledForm.Button
                  variant="contained"
                  type="reset"
                  color="primary"
                  onClick={() => {
                    setFieldValue("idArt", initialValues.idArt);
                    setFieldValue("nroReferencia", initialValues.nroReferencia);
                    setFieldValue("idConversacionExterna", initialValues.idConversacionExterna);
                    setFieldValue("documentoTrabajador", initialValues.documentoTrabajador);
                    setFieldValue("idEstadoConversacion", initialValues.idEstadoConversacion);
                    setFieldValue("incluirCerrados", initialValues.incluirCerrados);
                    setFieldValue("datePeriod", initialValues.datePeriod);
                    setFieldValue("dateFrom", initialValues.dateFrom);
                    setFieldValue("dateTo", initialValues.dateTo);
                    handleReset();
                  }}>
                  Limpiar
                </StyledForm.Button>
              </StyledForm.ButtonContainer>
            </>
          )}
        </FormikForm>
      </StyledPaper.Container>
    </div>
  );
};

export default MessagingSearchForm;
